import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../services/ApiService";
import AreaChart from "../../components/AreaChart";
import BarChart from "../../components/BarChart";
import DonutChart from "../../components/DonutChart";
import Loader from "../../components/Loader";
import AuthService from "../../services/AuthService";

import "../admin/Dashboard.css";

const fmtNumber = (value) => Number(value || 0).toLocaleString();
const fmtCurrency = (value) => `AED ${Number(value || 0).toLocaleString()}`;

// Booking-status palette (gold-forward to match theme)
const STATUS_META = [
  { key: "confirmed", label: "Confirmed", color: "#C99A3D", fields: ["confirmed_bookings"] },
  { key: "pending",   label: "Pending",   color: "#E8D2A6", fields: ["pending_bookings"] },
  { key: "completed", label: "Completed", color: "#9CAF6A", fields: ["completed_bookings"] },
  { key: "cancelled", label: "Cancelled", color: "#E08A76", fields: ["cancelled_bookings"] },
];

const pickStat = (stats, fields) => {
  for (const f of fields) {
    if (stats?.[f] !== undefined && stats?.[f] !== null) return Number(stats[f]) || 0;
  }
  return 0;
};

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [stats, setStats] = useState({});
  const [charts, setCharts] = useState({
    months: [],
    month_orders: [],
    month_revenue: [],
  });
  const [topServices, setTopServices] = useState([]);
  const [viewFormat, setViewFormat] = useState("monthly");

  const getDashboard = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: "GET",
        url: `partner/getDashboard?view_format=${viewFormat}`,
      });
      const data = response.data;
      if (data.status) {
        setStats(data.data.stats || {});
        setCharts(data.data.charts || {});
        setTopServices(data.data.top_services || []);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      // fail silently
    } finally {
      setIsLoader(false);
    }
  }, [viewFormat]);

  useEffect(() => {
    if (!AuthService.getCurrentpartner()) {
      navigate("/partner/login");
    }
    getDashboard();
  }, [navigate, getDashboard]);

  const totalRevenue =
    stats?.total_revenue ??
    stats?.total_sales ??
    (charts.month_revenue || []).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const statCards = [
    {
      label: "Active Bookings",
      value: fmtNumber(stats?.active_bookings),
      icon: "bi-stopwatch",
      tone: "tone-amber",
      onClick: () => navigate("/partner/bookings?status=active"),
    },
    {
      label: "Total Bookings",
      value: fmtNumber(stats?.total_bookings),
      icon: "bi-calendar2-check",
      tone: "tone-tan",
      onClick: () => navigate("/partner/bookings"),
    },
    {
      label: "Completed Bookings",
      value: fmtNumber(stats?.completed_bookings),
      icon: "bi-check2-circle",
      tone: "tone-green",
      sub: stats?.completion_rate != null ? `${stats.completion_rate}% completion rate` : null,
      onClick: () => navigate("/partner/bookings?status=complete"),
    },
    {
      label: "Total Revenue",
      value: fmtCurrency(totalRevenue),
      icon: "bi-cash-stack",
      tone: "tone-violet",
    },
  ];

  // Build booking-status breakdown from whatever real counts exist.
  const statusSegments = STATUS_META.map((m) => ({
    ...m,
    value: pickStat(stats, m.fields),
  })).filter((s) => s.value > 0);
  const statusTotal = statusSegments.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1 className="page-title">Partner Dashboard</h1>
          <p className="page-subtitle">An overview of your bookings and revenue.</p>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            {["weekly", "monthly", "yearly"].map((v) => (
              <button
                key={v}
                className={`toggle-btn ${viewFormat === v ? "active" : ""}`}
                onClick={() => setViewFormat(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {isLoader ? (
          <div className="loader-container">
            <Loader text="Loading Dashboard Data..." size="40" />
          </div>
        ) : (
          <>
            {/* Key Metrics Cards */}
            <div className="stat-grid">
              {statCards.map((card) => (
                <div
                  key={card.label}
                  className="stat-card"
                  onClick={card.onClick}
                  style={{ cursor: card.onClick ? "pointer" : "default" }}
                >
                  <div className="stat-card-top">
                    <div className={`stat-icon ${card.tone}`}>
                      <i className={`bi fs-1 ${card.icon}`}></i>
                    </div>
                    <div className="stat-meta">
                      <p className="stat-label">{card.label}</p>
                      <h3 className="stat-value">{card.value}</h3>
                    </div>
                  </div>
                  {card.sub && (
                    <div className="stat-change">
                      <span className="change-period">{card.sub}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Charts Row: Bookings + Status + Top Selling Services */}
            <div className="dash-row">
              <div className="dash-card flex-bookings">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Bookings Overview</h3>
                </div>
                <div className="dash-card-body chart-area">
                  <BarChart
                    labels={charts.months}
                    label="Bookings"
                    dataset={charts.month_orders}
                  />
                </div>
              </div>

              <div className="dash-card flex-status">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Booking Status</h3>
                </div>
                <div className="dash-card-body status-body">
                  {statusTotal > 0 ? (
                    <>
                      <div className="donut-wrap">
                        <DonutChart
                          data={statusSegments.map((s) => s.value)}
                          labels={statusSegments.map((s) => s.label)}
                          colors={statusSegments.map((s) => s.color)}
                        />
                        <div className="donut-center">
                          <span className="donut-total">{fmtNumber(statusTotal)}</span>
                          <span className="donut-total-label">Total</span>
                        </div>
                      </div>
                      <ul className="status-legend">
                        {statusSegments.map((s) => {
                          const pct = ((s.value / statusTotal) * 100).toFixed(1);
                          return (
                            <li key={s.key}>
                              <span className="legend-dot" style={{ background: s.color }}></span>
                              <span className="legend-label">{s.label}</span>
                              <span className="legend-value">
                                {fmtNumber(s.value)} <span className="legend-pct">({pct}%)</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="empty-state">No booking status data available</div>
                  )}
                </div>
              </div>

              <div className="dash-card flex-categories">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Top Selling Services</h3>
                  <Link to="/partner/services" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body">
                  {topServices.length > 0 ? (
                    <ul className="ts-list">
                      {topServices.map((service, index) => (
                        <li key={service._id || index} className="ts-item">
                          <span className={`ts-rank ts-rank-${index + 1}`}>{index + 1}</span>
                          <span className="ts-thumb">
                            {service.image ? (
                              <img src={service.image} alt={service.title} />
                            ) : (
                              <i className="bi bi-stars"></i>
                            )}
                          </span>
                          <span className="ts-info">
                            <span className="ts-name">{service.title}</span>
                            <span className="ts-meta">
                              {fmtNumber(service.totalBookings)} bookings
                            </span>
                          </span>
                          <span className="ts-revenue">{fmtCurrency(service.totalRevenue)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="empty-state">No services sold yet</div>
                  )}
                </div>
              </div>
            </div>

            {/* Revenue Overview */}
            <div className="dash-row">
              <div className="dash-card flex-revenue" style={{ flex: "1 1 100%" }}>
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Revenue Overview</h3>
                </div>
                <div className="dash-card-body">
                  <div className="revenue-summary">
                    <span className="revenue-label">Total Revenue</span>
                    <h2 className="revenue-value">{fmtCurrency(totalRevenue)}</h2>
                  </div>
                  <div className="chart-area">
                    <AreaChart
                      labels={charts.months}
                      label="Revenue"
                      dataset={charts.month_revenue}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
